export type TemplateCategory = 'planning' | 'meeting' | 'communication' | 'tracking' | 'reporting';

export const TEMPLATE_CATEGORIES: TemplateCategory[] = ['planning', 'meeting', 'communication', 'tracking', 'reporting'];

export interface BuiltInTemplate {
  id: string;
  name: string;
  description: string;
  category: TemplateCategory;
  content: string;
}

/** Starter templates available to every tenant; custom ones live in `project_templates`. */
export const BUILT_IN_TEMPLATES: BuiltInTemplate[] = [
  {
    id: 'builtin-kickoff',
    name: 'Project Kickoff Meeting',
    description: 'Agenda and notes for a project kickoff with stakeholders',
    category: 'meeting',
    content: `# Project Kickoff Meeting

**Project:**
**Date:**
**Facilitator:**
**Attendees:**

## Agenda
1. Welcome and introductions
2. Project background and objectives
3. Scope: in and out
4. Roles and responsibilities
5. Timeline and key milestones
6. Risks and assumptions
7. Communication plan and next steps

## Objectives
-

## Scope
**In scope:**
-

**Out of scope:**
-

## Key Milestones
| Milestone | Owner | Target date |
|-----------|-------|-------------|
| | | |

## Risks and Assumptions
-

## Decisions Made
-

## Action Items
| Action | Owner | Due |
|--------|-------|-----|
| | | |
`,
  },
  {
    id: 'builtin-schedule',
    name: 'Project Schedule Plan',
    description: 'Milestone and task schedule for tracking the project timeline',
    category: 'planning',
    content: `# Project Schedule Plan

**Project:**
**Project manager:**
**Baseline start:**
**Baseline end:**

## Milestones
| Milestone | Planned date | Forecast date | Status |
|-----------|--------------|---------------|--------|
| | | | |

## Work Breakdown
| Phase | Task | Owner | Start | End | Depends on |
|-------|------|-------|-------|-----|------------|
| | | | | | |

## Critical Path
-

## Schedule Risks
-
`,
  },
  {
    id: 'builtin-mom',
    name: 'Minutes of Meeting (MOM)',
    description: 'Record decisions, action items and attendees for any meeting',
    category: 'meeting',
    content: `# Minutes of Meeting

**Meeting:**
**Date and time:**
**Location or link:**
**Chair:**
**Minute taker:**

## Attendees
| Name | Role | Present |
|------|------|---------|
| | | |

## Discussion
### Topic 1
-

## Decisions
-

## Action Items
| Action | Owner | Due | Status |
|--------|-------|-----|--------|
| | | | Open |

## Next Meeting
**Date:**
**Agenda items:**
`,
  },
  {
    id: 'builtin-comms',
    name: 'Communication Matrix',
    description: 'Who needs what information, how often, and through which channel',
    category: 'communication',
    content: `# Communication Matrix

**Project:**
**Owner:**

| Stakeholder | Information needed | Frequency | Channel | Owner |
|-------------|--------------------|-----------|---------|-------|
| Sponsor | Status, risks, decisions needed | Weekly | Email | |
| Steering committee | Progress against milestones | Monthly | Meeting | |
| Project team | Tasks, blockers | Daily | Stand-up | |

## Escalation Path
1. Project manager
2. Programme manager
3. Sponsor

## Notes
-
`,
  },
  {
    id: 'builtin-raci',
    name: 'RACI Matrix',
    description: 'Responsible, accountable, consulted and informed roles across tasks',
    category: 'planning',
    content: `# RACI Matrix

**R** Responsible · **A** Accountable · **C** Consulted · **I** Informed

| Task or deliverable | Sponsor | Project manager | Team lead | Stakeholders |
|---------------------|---------|-----------------|-----------|--------------|
| Project charter | A | R | C | I |
| Requirements | I | A | R | C |
| Delivery plan | I | A | R | I |
| Go-live decision | A | R | C | I |

Each task has exactly one A.
`,
  },
  {
    id: 'builtin-status',
    name: 'Status Report',
    description: 'Weekly or monthly status report with progress, risks and next steps',
    category: 'reporting',
    content: `# Status Report

**Project:**
**Reporting period:**
**Prepared by:**
**Overall health:** Green / Amber / Red

## Summary
-

## Progress This Period
-

## Planned Next Period
-

## Milestones
| Milestone | Planned | Forecast | Status |
|-----------|---------|----------|--------|
| | | | |

## Budget
| Approved | Spent to date | Forecast at completion |
|----------|---------------|------------------------|
| | | |

## Risks and Issues
| Item | Severity | Owner | Mitigation |
|------|----------|-------|------------|
| | | | |

## Decisions Needed
-
`,
  },
  {
    id: 'builtin-risk',
    name: 'Risk Register',
    description: 'Identify, assess and track project risks and mitigations',
    category: 'tracking',
    content: `# Risk Register

**Project:**
**Last reviewed:**

| ID | Risk | Likelihood (1-5) | Impact (1-5) | Score | Mitigation | Owner | Status |
|----|------|------------------|--------------|-------|------------|-------|--------|
| R1 | | | | | | | Open |

Score = likelihood × impact. Review scores of 15 or more at every status meeting.
`,
  },
  {
    id: 'builtin-charter',
    name: 'Project Charter',
    description: 'Objectives, scope, stakeholders and governance for a new project',
    category: 'planning',
    content: `# Project Charter

**Project:**
**Sponsor:**
**Project manager:**
**Date:**

## Purpose and Business Case
-

## Objectives
-

## Scope
**In scope:**
-

**Out of scope:**
-

## Deliverables
-

## Stakeholders
| Name | Role | Interest |
|------|------|----------|
| | | |

## Budget and Timeline
**Budget:**
**Start:**
**End:**

## Assumptions and Constraints
-

## Governance and Approvals
-
`,
  },
  {
    id: 'builtin-decision-log',
    name: 'Decision Log',
    description: 'Keep a dated record of decisions, owners and rationale',
    category: 'tracking',
    content: `# Decision Log

**Project:**

| ID | Date | Decision | Rationale | Decided by | Impact |
|----|------|----------|-----------|------------|--------|
| D1 | | | | | |
`,
  },
];
